import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Sparkles, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
    "What shoes do you have?",
    "I like blue and bold styles — what suits me?",
    "Which is the cheapest pair?",
    "What football-themed designs are available?",
];

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const ProductCard = ({ product }) => (
    <div data-testid={`vault-ai-card-${product.id}`} className="border border-vault-border bg-white">
        <div className="border-b border-vault-border p-4">
            <img src={product.image} alt={product.name} className="h-36 w-full object-contain" />
        </div>
        <div className="p-4">
            <p className="font-display text-lg uppercase text-stone-900">{product.name}</p>
            <p className="mt-1 font-mono text-sm text-stone-900">
                <span className="mr-2 text-xs text-stone-400 line-through">{inr(product.original_price)}</span>
                {inr(product.price)}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-stone-600 line-clamp-3">{product.description}</p>
            <Link
                data-testid={`view-product-${product.id}`}
                to={`/?focus=${product.id}`}
                className="btn-sweep mt-4 flex items-center justify-center gap-2 border border-vault-red px-4 py-2.5 font-mono text-[10px] tracking-[0.25em] text-stone-900"
            >
                VIEW PRODUCT <ArrowRight size={12} />
            </Link>
        </div>
    </div>
);

export default function VaultAI() {
    const [products, setProducts] = useState({});
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [thinking, setThinking] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem("vault_ai_session") || "");
    const bottomRef = useRef(null);

    useEffect(() => {
        axios
            .get(`${API}/products`)
            .then((r) => {
                const map = {};
                r.data.forEach((p) => (map[p.id] = p));
                setProducts(map);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, thinking]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || thinking) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: msg }]);
        setThinking(true);
        try {
            const { data } = await axios.post(`${API}/vault-ai/chat`, { session_id: sessionId, message: msg });
            if (data.session_id && data.session_id !== sessionId) {
                setSessionId(data.session_id);
                localStorage.setItem("vault_ai_session", data.session_id);
            }
            setMessages((m) => [...m, { role: "assistant", content: data.reply, ids: data.recommended_ids || [] }]);
        } catch {
            toast.error("Vault AI lost its train of thought — try again");
            setMessages((m) => [
                ...m,
                { role: "assistant", content: "Something glitched on my end — ask me again in a moment.", ids: [] },
            ]);
        } finally {
            setThinking(false);
        }
    };

    return (
        <div data-testid="vault-ai-page" className="min-h-screen bg-vault-black text-stone-900">
            <Header />
            <main className="mx-auto max-w-3xl px-6 pb-24 pt-28">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-10 border-b border-vault-border pb-8"
                >
                    <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] text-vault-red">
                        <Sparkles size={14} /> PERSONAL SHOE STYLIST
                    </p>
                    <h1 className="mt-3 font-display text-5xl uppercase leading-none md:text-6xl">Vault AI</h1>
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-600">
                        Tell me your colors, your outfits, your club — I know every pair in the Vault and I&apos;ll find
                        the one painted for you.
                    </p>
                </motion.div>

                {messages.length === 0 && (
                    <div className="mb-8 grid gap-2 sm:grid-cols-2">
                        {SUGGESTIONS.map((s) => (
                            <button
                                key={s}
                                data-testid={`suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                                onClick={() => send(s)}
                                className="border border-vault-border bg-white px-4 py-3.5 text-left font-mono text-xs leading-relaxed text-stone-600 transition-colors hover:border-vault-red hover:text-stone-900"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}

                <div data-testid="vault-ai-messages" className="space-y-6">
                    {messages.map((m, i) =>
                        m.role === "user" ? (
                            <div key={i} className="flex justify-end">
                                <p className="max-w-[85%] border border-vault-red/50 bg-vault-surface/60 px-5 py-3.5 text-sm text-stone-900">
                                    {m.content}
                                </p>
                            </div>
                        ) : (
                            <div key={i} className="flex gap-3">
                                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center border border-vault-red font-display text-sm text-vault-red">
                                    V
                                </span>
                                <div className="min-w-0 max-w-[90%]">
                                    <p className="whitespace-pre-line border border-vault-border bg-white px-5 py-4 text-sm leading-relaxed text-stone-900">
                                        {m.content}
                                    </p>
                                    {m.ids && m.ids.length > 0 && (
                                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                            {m.ids.map((id) =>
                                                products[id] ? <ProductCard key={id} product={products[id]} /> : null,
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ),
                    )}
                    {thinking && (
                        <div data-testid="vault-ai-typing" className="flex gap-3">
                            <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center border border-vault-red font-display text-sm text-vault-red">
                                V
                            </span>
                            <p className="flex items-center gap-1.5 border border-vault-border bg-white px-5 py-4">
                                {[0, 1, 2].map((d) => (
                                    <span
                                        key={d}
                                        className="h-1.5 w-1.5 animate-pulse rounded-full bg-vault-red"
                                        style={{ animationDelay: `${d * 0.2}s` }}
                                    />
                                ))}
                            </p>
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>

                <div className="sticky bottom-4 mt-10 border border-vault-border bg-white/90 shadow-lg backdrop-blur-xl">
                    <div className="flex items-center gap-3 p-3">
                        <input
                            data-testid="vault-ai-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            placeholder="Ask about colors, styles, clubs, prices..."
                            className="flex-1 bg-transparent px-3 py-2.5 text-sm text-stone-900 placeholder-stone-400"
                        />
                        <button
                            data-testid="vault-ai-send-button"
                            onClick={() => send()}
                            disabled={thinking || !input.trim()}
                            aria-label="Send message"
                            className="btn-sweep flex items-center gap-2 border border-vault-red px-5 py-2.5 font-mono text-xs tracking-[0.2em] text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <Send size={14} /> ASK
                        </button>
                    </div>
                </div>
                <p className="mt-4 text-center font-mono text-[10px] tracking-[0.2em] text-stone-400">
                    VAULT AI KNOWS ONLY THE LIVE VAULT CATALOGUE — NO GUESSWORK
                </p>
            </main>
            <Footer />
        </div>
    );
}
